import {
  Box, Chip, Divider, InputAdornment, Link, ListItemButton, TextField, Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FixedSizeList } from 'react-window'
import PageModal from 'new-components/modal/page-modal'
import InputCheckbox from 'component/Input/InputCheckbox'
import type { ProcedureRef } from 'api/executive/executive.types'

interface ProcedurePickerModalProps {
  open: boolean
  onClose: () => void
  onConfirm: (selectedIds: number[]) => void
  procedures: ProcedureRef[]
  alreadyAddedIds: number[]
}

type ListItem = { kind: 'divider' } | { kind: 'option'; row: ProcedureRef }

export default function ProcedurePickerModal(props: ProcedurePickerModalProps) {
  const { open, onClose, onConfirm, procedures, alreadyAddedIds } = props
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [showSelectedOnly, setShowSelectedOnly] = useState(false)

  // Debounce search input
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search), 200)
    return () => clearTimeout(handle)
  }, [search])

  // Reset transient state when modal closes
  useEffect(() => {
    if (!open) {
      setSearch('')
      setDebouncedSearch('')
      setSelectedIds(new Set())
      setShowSelectedOnly(false)
    }
  }, [open])

  const alreadyAddedSet = useMemo(() => new Set(alreadyAddedIds), [alreadyAddedIds])

  const filteredMatches = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase()
    const list = q
      ? procedures.filter((p) =>
          p.name.toLowerCase().includes(q) ||
          (p.code ? p.code.toLowerCase().includes(q) : false),
        )
      : procedures.slice()
    // Sort by code (natural, numeric-aware so "01-2" precedes "01-11");
    // fall back to name when a procedure has no code.
    return list.sort((a, b) => {
      const ka = a.code || a.name
      const kb = b.code || b.name
      return ka.localeCompare(kb, undefined, { numeric: true, sensitivity: 'base' })
    })
  }, [procedures, debouncedSearch])

  /** Pinned items are already-added (persistent) + currently-checked (new). */
  const listItems: ListItem[] = useMemo(() => {
    const picked = filteredMatches.filter(
      (p) => alreadyAddedSet.has(p.id) || selectedIds.has(p.id),
    )
    if (showSelectedOnly) {
      return picked.map((row) => ({ kind: 'option', row }))
    }
    const rest = filteredMatches.filter(
      (p) => !alreadyAddedSet.has(p.id) && !selectedIds.has(p.id),
    )
    const items: ListItem[] = picked.map((row) => ({ kind: 'option', row }))
    if (picked.length > 0 && rest.length > 0) items.push({ kind: 'divider' })
    rest.forEach((row) => items.push({ kind: 'option', row }))
    return items
  }, [filteredMatches, alreadyAddedSet, selectedIds, showSelectedOnly])

  const toggle = (id: number) => {
    if (alreadyAddedSet.has(id)) return
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  /** Select every filtered procedure that isn't already added or already selected. */
  const selectAllFiltered = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      filteredMatches.forEach((p) => {
        if (!alreadyAddedSet.has(p.id)) next.add(p.id)
      })
      return next
    })
  }

  /** Remove from `selectedIds` every filtered procedure (already-added stays). */
  const clearFilteredSelection = () => {
    const filteredIds = new Set(filteredMatches.map((p) => p.id))
    setSelectedIds((prev) => {
      const next = new Set<number>()
      prev.forEach((id) => {
        if (!filteredIds.has(id)) next.add(id)
      })
      return next
    })
  }

  const handleConfirm = () => {
    onConfirm(Array.from(selectedIds))
  }

  const Row = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    const item = listItems[index]
    if (!item) return null
    if (item.kind === 'divider') {
      return (
        <Box style={style} sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
          <Divider sx={{ width: '100%' }} />
        </Box>
      )
    }
    const p = item.row
    const isAlready = alreadyAddedSet.has(p.id)
    const isChecked = isAlready || selectedIds.has(p.id)
    return (
      <ListItemButton
        style={style}
        disabled={isAlready}
        onClick={() => toggle(p.id)}
        sx={{ pl: 1, pr: 2 }}
      >
        <InputCheckbox checked={isChecked} disabled={isAlready} />
        {p.code && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mr: 1.5, minWidth: 56, fontFamily: 'monospace' }}
          >
            {p.code}
          </Typography>
        )}
        <Typography sx={{ flexGrow: 1, mr: 1 }} noWrap>
          {p.name}
        </Typography>
        {isAlready && (
          <Chip
            size="small"
            label={t('DASHBOARD_TARGETS.PICKER.ALREADY_ADDED', { defaultValue: 'เพิ่มแล้ว' })}
          />
        )}
      </ListItemButton>
    )
  }

  const selectableMatchCount = filteredMatches.filter((p) => !alreadyAddedSet.has(p.id)).length

  return (
    <PageModal
      isOpen={open}
      size="M"
      title={t('DASHBOARD_TARGETS.PICKER.TITLE', { defaultValue: 'เลือกหัตถการที่ต้องการติดตาม' }) as string}
      handleClose={onClose}
      onCancel={{
        onClick: onClose,
        title: t('DASHBOARD_TARGETS.PICKER.CANCEL', { defaultValue: 'ยกเลิก' }) as string,
      }}
      onSubmit={{
        onClick: handleConfirm,
        title: t('DASHBOARD_TARGETS.PICKER.ADD_N', {
          defaultValue: 'เพิ่ม {{n}} รายการ',
          n: selectedIds.size,
        }) as string,
        disabled: selectedIds.size === 0,
        width: 180,
      }}
    >
      <TextField
        autoFocus
        fullWidth
        size="small"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={
          t('DASHBOARD_TARGETS.PICKER.SEARCH_PLACEHOLDER', { defaultValue: 'ค้นหาหัตถการ หรือ code…' }) as string
        }
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 1 }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
        <InputCheckbox
          checked={showSelectedOnly}
          onChange={(e: any) => setShowSelectedOnly(Boolean(e.target.checked))}
          label={t('DASHBOARD_TARGETS.PICKER.SHOW_SELECTED_ONLY', { defaultValue: 'แสดงเฉพาะที่เลือก' })}
        />
        <Box sx={{ flexGrow: 1 }} />
        {selectableMatchCount > 0 && (
          <Link component="button" variant="caption" onClick={selectAllFiltered}>
            {t('DASHBOARD_TARGETS.PICKER.SELECT_ALL_FILTERED', {
              defaultValue: 'เลือกทั้งหมดที่กรอง ({{n}})',
              n: selectableMatchCount,
            })}
          </Link>
        )}
        {selectedIds.size > 0 && (
          <Link component="button" variant="caption" color="text.secondary" onClick={clearFilteredSelection}>
            {t('DASHBOARD_TARGETS.PICKER.CLEAR_FILTERED', { defaultValue: 'ล้างที่เลือก' })}
          </Link>
        )}
      </Box>
      {selectedIds.size > 0 && (
        <Box sx={{ mb: 1 }}>
          <Chip
            color="primary"
            size="small"
            label={t('DASHBOARD_TARGETS.PICKER.SELECTED_COUNT', {
              defaultValue: 'เลือกแล้ว {{n}} รายการ',
              n: selectedIds.size,
            })}
          />
        </Box>
      )}
      {listItems.length === 0 ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {showSelectedOnly
              ? t('DASHBOARD_TARGETS.PICKER.EMPTY_SELECTED', { defaultValue: 'ยังไม่มีรายการที่เลือก' })
              : t('DASHBOARD_TARGETS.PICKER.EMPTY', {
                  defaultValue: 'ไม่พบหัตถการที่ตรงกับ "{{q}}"',
                  q: debouncedSearch,
                })}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ height: '50vh' }}>
          <FixedSizeList
            height={Math.floor(window.innerHeight * 0.5)}
            width="100%"
            itemSize={44}
            itemCount={listItems.length}
            overscanCount={6}
          >
            {Row}
          </FixedSizeList>
        </Box>
      )}
    </PageModal>
  )
}
